import { CircularProgress } from '@mui/material'
import { getSignatureImageCard } from 'api'
import { Button, Icon, Typography } from 'components'
import { updatePhoto } from 'components/slideImageContent'
import { useDebounceEffect, useToast } from 'hooks'
import { Icons, PercentCrop, ProfileImages, TypographyTypes } from 'interfaces'
import React, { useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import {
	centerCrop,
	Crop,
	makeAspectCrop,
	PixelCrop,
	ReactCrop
} from 'react-image-crop'
import Cookies from 'universal-cookie'
import { canvasPreview, configGenaral } from 'utils'

export const AddImageCard: React.FC<{
	image: string
	setOpen: React.Dispatch<React.SetStateAction<boolean>>
	setImage: React.Dispatch<React.SetStateAction<string>>
}> = ({ image, setOpen, setImage }): JSX.Element => {
	const { showToast } = useToast()
	const cookies = new Cookies()
	const organizationId = cookies.get('organization_id')
	const aspect = 1 / 0.3257
	const MIN_WIDTH_DIMENSION = 1500
	const MIN_HEIGHT_DIMENSION = 488
	const previewCanvasRef = useRef<HTMLCanvasElement | null>(null)
	const imgRef = useRef<HTMLImageElement>(null)
	const [crop, setCrop] = useState<Crop | null>()
	const [completedCrop, setCompletedCrop] = useState<PixelCrop | null>()
	const [isCropComplete, setIsCropComplete] = useState(false)
	const [isActiveFinish, setIsActiveFinish] = useState(false)
	const [scale, setScale] = useState(1)
	const [isCropSettingsVisible, setIsCropSettingsVisible] = useState(true)
	const [isImageUploaded, setIsImageUploaded] = useState(false)
	const [loading, setLoading] = useState(false)
	const [nameImage, setNameImage] = useState<string | null>(null)
	const [isFinishCropButtonVisible, setIsFinishCropButtonVisible] =
		useState(true)
	const [croppedImage, setCroppedImage] = useState<string | undefined>(
		image ? `${image}?timestamp=${Date.now()}` : undefined
	)

	const { register } = useForm<ProfileImages>({
		mode: 'all'
	})

	const registerInputCover = register('coverUrl', {
		required: { value: true, message: 'Is required' }
	})

	const handleFinishCrop = (): void => {
		if (completedCrop && previewCanvasRef.current) {
			const canvas = previewCanvasRef.current
			const resizedCanvas = document.createElement('canvas')
			const resizedCtx = resizedCanvas.getContext('2d')

			resizedCanvas.width = MIN_WIDTH_DIMENSION
			resizedCanvas.height = MIN_HEIGHT_DIMENSION

			if (resizedCtx !== null) {
				resizedCtx.imageSmoothingEnabled = false
				resizedCtx.drawImage(
					canvas,
					0,
					0,
					MIN_WIDTH_DIMENSION,
					MIN_HEIGHT_DIMENSION
				)
			}

			const croppedImageData = resizedCanvas.toDataURL('image/png')

			setCroppedImage(croppedImageData)
			setIsCropSettingsVisible(false)
			setIsFinishCropButtonVisible(false)
			setIsCropComplete(true)
			setScale(1)
		}
	}

	useDebounceEffect(
		async () => {
			if (
				completedCrop?.width &&
				completedCrop?.height &&
				imgRef.current &&
				previewCanvasRef.current
			) {
				canvasPreview(
					imgRef.current,
					previewCanvasRef.current,
					completedCrop,
					scale
				)
			}
		},
		500,
		[completedCrop, scale]
	)

	const centerAspectCrop = (
		mediaWidth: number,
		mediaHeight: number
	): PercentCrop => {
		return centerCrop(
			makeAspectCrop(
				{
					unit: '%',
					width: 100
				},
				aspect,
				mediaWidth,
				mediaHeight
			),
			mediaWidth,
			mediaHeight
		)
	}

	const resetCrop = (): void => {
		setCrop(null)
		setCompletedCrop(null)
	}

	const saveProfileImages = async (): Promise<void> => {
		setLoading(true)
		let imageCard = null
		const credentials = await getSignatureImageCard(organizationId)
		if (croppedImage && nameImage) {
			imageCard = await updatePhoto(croppedImage, credentials, nameImage)
		}
		if (imageCard) setImage(imageCard)
		setOpen(false)
		setLoading(false)
	}

	const handleMediaUploadCover = (file: File): void => {
		const fileSize = file.size
		const fileMb = fileSize / 1024 ** 2

		if (fileMb <= configGenaral.maxfileSizeMB) {
			const reader = new FileReader()
			reader.onload = async e => {
				const FileImage = new Image()
				FileImage.onload = () => {
					if (
						FileImage.width >= MIN_WIDTH_DIMENSION &&
						FileImage.height >= MIN_HEIGHT_DIMENSION
					) {
						const uploadedObj = { file, url: e.target?.result as string }
						setNameImage(uploadedObj.file.name)
						setCroppedImage(uploadedObj.url)
						setIsImageUploaded(true)
						const { width, height } = FileImage
						const newCrop = centerAspectCrop(width, height)
						setCrop(newCrop)
					} else {
						showToast(
							'Error',
							`Please upload an image with dimensions of at least ${MIN_WIDTH_DIMENSION}x${MIN_HEIGHT_DIMENSION} pixels.`,
							'error'
						)
					}
				}
				FileImage.src = e.target?.result as string
			}
			reader.readAsDataURL(file)
		} else {
			showToast(
				'Error',
				`Please select a file less than ${configGenaral.maxfileSizeMB}MB.`,
				'error'
			)
		}
	}

	const handleDeleteImage = (): void => {
		setNameImage(null)
		setCroppedImage(undefined)
		resetCrop()
		setIsImageUploaded(false)
		setIsCropSettingsVisible(true)
		setIsFinishCropButtonVisible(true)
		setIsCropComplete(false)
		setIsActiveFinish(false)
		setScale(1)
	}
	const handleControlZoom = (): void => {
		if (completedCrop) {
			if (scale <= 0.5) {
				setScale(0.5)
			}
		}
	}

	if (loading)
		return (
			<div className="flex h-full w-full justify-center items-center">
				<CircularProgress disableShrink />
			</div>
		)

	return (
		<form className="mt-8 flex flex-col justify-between h-[95%]">
			<div className="py-5 flex flex-col gap-6">
				{croppedImage !== undefined && (
					<div className="relative ">
						<div className=" flex justify-center !items-center relative w-full">
							<ReactCrop
								crop={crop as Crop}
								aspect={aspect}
								onChange={(_, percentCrop) => {
									setCrop(percentCrop)
									setIsActiveFinish(false)
								}}
								onComplete={c => {
									setCompletedCrop(c)
									setIsActiveFinish(true)
								}}
								locked
							>
								<img
									src={croppedImage}
									alt="Cover"
									className="w-full h-auto !max-h-[19rem] object-contain"
									style={{
										transform: `scale(${scale})`
									}}
									ref={imgRef}
								/>
							</ReactCrop>
							{croppedImage !== undefined && (
								<button
									type="button"
									onClick={() => handleDeleteImage()}
									className="absolute top-1 right-2 bg-gray-18 p-1 rounded-full border-2 border-gray-3"
								>
									<Icon src={Icons.close} className="w-3 h-3 " />
								</button>
							)}
						</div>
					</div>
				)}

				{isCropSettingsVisible && isImageUploaded && (
					<div className="flex flex-col border-blue-primary justify-center w-[550px] pl-2 h-[100px] border-solid border-2 rounded-lg m-2 p-2">
						<Typography
							fontLeading="15/18"
							type={TypographyTypes.p}
							className="!font-normal text-black-1 mt-2 ml-2"
							title="Zoom:"
						/>
						<div className="flex flex-row">
							<div className="flex justify-around w-full m-2 p-2">
								<Button
									type="button"
									value={scale}
									onClick={() => setScale(prevScale => prevScale + 0.01)}
									className="min-w-[40px] !py-1"
									color="Transparent"
									disabled={!completedCrop}
								>
									<Icon src={Icons.zoomIn} className="w-[25px]	h-[25px]" />
								</Button>
								<Button
									type="button"
									value={scale}
									onClick={() => {
										setScale(prevScale => prevScale - 0.01)
										handleControlZoom()
									}}
									className="min-w-[40px] !py-1"
									color="Transparent"
									disabled={!completedCrop}
								>
									<Icon src={Icons.zoomOut} className="w-[25px] h-[25px]" />
								</Button>
							</div>
						</div>
					</div>
				)}
				{isFinishCropButtonVisible && isImageUploaded && (
					<div className="pt-2">
						<Button
							label="Finish Crop"
							type="button"
							color="Primary"
							className="w-full mt-1"
							onClick={() => {
								handleFinishCrop()
								setIsCropSettingsVisible(false)
								setIsFinishCropButtonVisible(false)
								resetCrop()
							}}
							disabled={!isActiveFinish}
						/>
					</div>
				)}
				{!!completedCrop && (
					<canvas
						id="croppedCanvas"
						className="h-0 w-0 rounded-lg border-blue-300"
						ref={previewCanvasRef}
						style={{
							visibility: 'hidden',
							display: 'hidden',
							objectFit: 'contain'
						}}
					/>
				)}
				{croppedImage === undefined && (
					<div className="flex justify-end">
						<input
							id="coverUrl"
							type="file"
							className="hidden"
							{...registerInputCover}
							onChange={event => {
								if (event.target.files) {
									handleMediaUploadCover(event.target.files[0])
									const input = event.target
									input.value = ''
								}
							}}
							accept="image/png, image/jpeg, image/jpg"
							maxLength={10}
						/>

						<Button
							label="Upload"
							color="Transparent"
							iconLeft={Icons.addPhoto}
							onClick={() => {
								const input = document.getElementById('coverUrl')
								if (input) input.click()
							}}
						/>
					</div>
				)}
			</div>
			<div className="w-full flex justify-between items-center">
				<Button
					label="Cancel"
					color="Gray2"
					small
					onClick={() => setOpen(false)}
				/>
				<Button
					label="Save"
					small
					onClick={() => saveProfileImages()}
					disabled={!croppedImage || !isCropComplete}
				/>
			</div>
		</form>
	)
}
